import { useCallback, useEffect, useState } from "react";

/**
 * A per-browser preference, such as the folded sidebar. Read after the first render and written
 * on change; storage that's blocked or full is ignored, never an error.
 */
export function usePersistentFlag(key: string, initial: boolean): [boolean, (value: boolean) => void] {
  const [value, setValue] = useState(initial);
  useEffect(() => {
    const stored = readFlag(key);
    if (stored !== null) setValue(stored);
  }, [key]);
  const update = useCallback(
    (next: boolean) => {
      setValue(next);
      writeFlag(key, next);
    },
    [key],
  );
  return [value, update];
}

export function readFlag(
  key: string,
  storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage,
): boolean | null {
  try {
    const raw = storage?.getItem(key);
    return raw === "true" ? true : raw === "false" ? false : null;
  } catch {
    return null;
  }
}

export function writeFlag(
  key: string,
  value: boolean,
  storage: Pick<Storage, "setItem"> | undefined = globalThis.localStorage,
) {
  try {
    storage?.setItem(key, String(value));
  } catch {
    // private mode or full storage: the preference just isn't remembered
  }
}
